import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import type { CreateRestrictionRequest, RestrictionType } from '@yawi/types';

interface RestrictionFormProps {
  onSubmit: (data: CreateRestrictionRequest) => Promise<unknown>;
  loading?: boolean;
}

export const RestrictionForm = ({ onSubmit, loading }: RestrictionFormProps) => {
  const [type, setType] = useState<RestrictionType>('PERSON');
  const [name, setName] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: CreateRestrictionRequest = {
      type,
      reason,
      name: name || undefined,
      documentId: documentId || undefined,
      licensePlate: licensePlate || undefined,
      expiresAt: expiresAt || undefined,
    };

    await onSubmit(data);

    // Reset form
    setName('');
    setDocumentId('');
    setLicensePlate('');
    setReason('');
    setExpiresAt('');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">Nueva Restriccion</h3>

      <div className="space-y-4">
        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Tipo de restriccion
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as RestrictionType)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="PERSON">Persona</option>
            <option value="VEHICLE">Vehiculo</option>
            <option value="GENERAL">General</option>
          </select>
        </div>

        {/* Conditional fields based on type */}
        {type === 'PERSON' && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Numero de identificacion (opcional)
              </label>
              <input
                type="text"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="INE, pasaporte, etc."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </>
        )}

        {type === 'VEHICLE' && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Placa del vehiculo
              </label>
              <input
                type="text"
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                placeholder="ABC-123"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descripcion del vehiculo (opcional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Marca, modelo, color..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </>
        )}

        {type === 'GENERAL' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descripcion
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Descripcion de la restriccion"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}

        {/* Reason - always required */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Motivo *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Razon de la restriccion..."
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={2}
          />
        </div>

        {/* Expiration date */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Fecha de expiracion (opcional)
          </label>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading || !reason}
          className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Plus className="w-5 h-5" />
              Agregar Restriccion
            </>
          )}
        </button>
      </div>
    </form>
  );
};
